import { v4 as uuid } from "uuid"
import {
  BaseType,
  Cardinality,
  InteractionTypes,
  QtiContainerType,
  ResponseDeclaration
} from "./helpers/types"

export const EQUATION_EDITOR_SECTIONS = {
  equationNumbersSection: {
    equationOperators: [
      {
        attributes: { id: "numbers", type: "numbers", label: "Numbers" },
        equationOperator: [
          { attributes: { tex: "1" } },
          { attributes: { tex: "2" } },
          { attributes: { tex: "3" } },
          { attributes: { tex: "4" } },
          { attributes: { tex: "5" } },
          { attributes: { tex: "6" } },
          { attributes: { tex: "7" } },
          { attributes: { tex: "8" } },
          { attributes: { tex: "9" } },
          { attributes: { tex: "0" } }
        ]
      },
      {
        attributes: { id: "numbersExtra", label: "Signs" },
        equationOperator: [
          { attributes: { tex: "." } },
          { attributes: { tex: "-" } },
          { attributes: { tex: "/" } }
        ]
      }
    ]
  },
  equationActionsSection: {
    equationOperators: [
      {
        attributes: { id: "symbols", type: "symbols", label: "Symbols" },
        equationOperator: [
          { attributes: { tex: "x" } },
          { attributes: { tex: "y" } },
          { attributes: { tex: "z" } }
        ]
      },
      {
        attributes: { id: "basicOps", label: "Basic Operations" },
        equationOperator: [
          { attributes: { tex: "+" } },
          { attributes: { tex: "-" } },
          { attributes: { tex: "*" } },
          { attributes: { tex: "/" } }
        ]
      },
      {
        attributes: { id: "comparison", label: "Comparison" },
        equationOperator: [
          { attributes: { tex: "<" } },
          { attributes: { tex: "\\le" } },
          { attributes: { tex: "=" } },
          { attributes: { tex: "\\ge" } },
          { attributes: { tex: ">" } }
        ]
      },
      {
        attributes: { id: "extraOps", label: "Extra Operators" },
        equationOperator: [
          { attributes: { tex: "^" } },
          { attributes: { tex: "_" } },
          { attributes: { tex: "(" } },
          { attributes: { tex: "\\sqrt" } },
          { attributes: { tex: "\\pi" } }
        ]
      }
    ]
  }
}

export const OPERATORS_LABELS = {
  "*": "\\times",
  "^": "a^b",
  _: "a_b",
  "(": "()",
  "\\sqrt": "\\sqrt{b}"
}

export const INITIAL_DATA = {
  default(responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId || uuid()
      }
    }
  },
  [InteractionTypes.choice](responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId || uuid(),
        shuffle: false,
        maxChoices: 1
      },
      prompt: "",
      simpleChoice: [
        {
          attributes: {
            identifier: uuid()
          },
          data: ""
        }
      ]
    }
  },
  [InteractionTypes.extendedText](responseId?: string, containerType?: QtiContainerType) {
    const isPracticeTest = containerType === "practiceTest"
    return {
      attributes: {
        responseIdentifier: responseId || uuid(),
        neutralSetting: !isPracticeTest
      },
      prompt: "",
      content: {
        content: { ops: [{ insert: "\n" }] },
        enableRichText: !isPracticeTest,
        characterCount: isPracticeTest ? "9550" : "450",
        limitDisplay: "count",
        enableCrase: false,
        craseId: ""
      }
    }
  },
  [InteractionTypes.match](responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId || uuid(),
        shuffle: false,
        minAssociations: 0
        // "maxAssociations": 0
      },
      prompt: "",
      simpleMatchSet: [
        {
          simpleAssociableChoice: [
            {
              attributes: {
                identifier: uuid(),
                matchMin: 0,
                matchMax: 0,
                showHide: "show",
                fixed: false
              },
              data: "",
              deltas: { ops: [{ insert: "\n" }] }
            },
            {
              attributes: {
                identifier: uuid(),
                matchMin: 0,
                matchMax: 0,
                showHide: "show",
                fixed: false
              },
              data: "",
              deltas: { ops: [{ insert: "\n" }] }
            }
          ]
        },
        {
          simpleAssociableChoice: [
            {
              attributes: {
                identifier: uuid(),
                matchMin: 0,
                matchMax: 0,
                showHide: "show",
                fixed: false
              },
              data: "",
              deltas: { ops: [{ insert: "\n" }] }
            },
            {
              attributes: {
                identifier: uuid(),
                matchMin: 0,
                matchMax: 0,
                showHide: "show",
                fixed: false
              },
              data: "",
              deltas: { ops: [{ insert: "\n" }] }
            }
          ]
        }
      ]
    }
  },
  [InteractionTypes.gapMatch](responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId || uuid(),
        shuffle: false,
        minAssociations: 0
        // "maxAssociations": 0
      },
      prompt: "",
      gapText: [
        {
          attributes: {
            identifier: uuid(),
            matchMin: 0
            // "matchMax": 0
          },
          data: ""
        }
      ],
      content: {
        deltas: { ops: [{ insert: "\n" }] }
      }
    }
  },
  [InteractionTypes.hottext](responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId || uuid(),
        maxChoices: 1
      },
      prompt: "",
      content: {
        deltas: { ops: [{ insert: "\n" }] }
      }
    }
  },
  [InteractionTypes.equationEditor](responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId || uuid(),
        id: InteractionTypes.equationEditor
      },
      prompt: "",
      equationNumbersSection: EQUATION_EDITOR_SECTIONS.equationNumbersSection,
      equationActionsSection: {}
    }
  },
  [InteractionTypes.hotspot](responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId || uuid(),
        minChoices: 0,
        maxChoices: 1
      },
      prompt: "",
      object: {
        attributes: {
          data: "",
          cropData: null,
          fileName: "",
          type: "",
          width: null,
          height: null
        }
      },
      hotspotChoice: []
    }
  },
  [InteractionTypes.order](responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId || uuid(),
        shuffle: true,
        orientation: "vertical"
      },
      prompt: "",
      simpleChoice: [
        {
          attributes: {
            identifier: uuid()
          },
          data: ""
        }
      ]
    }
  },
  [InteractionTypes.inlineChoice](responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId ?? uuid()
      },
      prompt: "",
      content: {
        deltas: { ops: [{ insert: "\n" }] }
      }
    }
  },
  [InteractionTypes.textEntry](responseId?: string) {
    return {
      attributes: {
        responseIdentifier: responseId ?? uuid()
      },
      prompt: "",
      content: {
        deltas: { ops: [{ insert: "\n" }] }
      }
    }
  }
}

export const INITIAL_RESPONSE_DECLARATION: Record<
  InteractionTypes | "default",
  (responseId?: string) => ResponseDeclaration
> = {
  default(responseId?: string) {
    return {
      attributes: {
        identifier: responseId || uuid()
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.choice](responseId?: string) {
    return {
      attributes: {
        identifier: responseId || uuid(),
        baseType: BaseType.identifier,
        cardinality: Cardinality.single
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.match](responseId?: string) {
    return {
      attributes: {
        identifier: responseId || uuid(),
        baseType: BaseType.directedPair,
        cardinality: Cardinality.multiple
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.gapMatch](responseId?: string) {
    return {
      attributes: {
        identifier: responseId || uuid(),
        baseType: BaseType.directedPair,
        cardinality: Cardinality.multiple
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.hottext](responseId?: string) {
    return {
      attributes: {
        identifier: responseId || uuid(),
        baseType: BaseType.identifier,
        cardinality: Cardinality.single
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.custom](responseId?: string) {
    return {
      attributes: {
        identifier: responseId || uuid(),
        baseType: BaseType.string,
        cardinality: Cardinality.single
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.equationEditor](responseId?: string) {
    return {
      // TODO: ???
      attributes: {
        identifier: responseId || uuid(),
        baseType: BaseType.string,
        cardinality: Cardinality.single
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.hotspot](responseId?: string) {
    return {
      attributes: {
        identifier: responseId || uuid(),
        baseType: BaseType.identifier,
        cardinality: Cardinality.single
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.extendedText](responseId?: string) {
    return {
      attributes: {
        identifier: responseId || uuid(),
        baseType: BaseType.identifier,
        cardinality: Cardinality.single
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.order](responseId?: string) {
    return {
      attributes: {
        identifier: responseId || uuid(),
        baseType: BaseType.identifier,
        cardinality: Cardinality.ordered
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.inlineChoice](responseId?: string) {
    return {
      attributes: {
        identifier: responseId ?? uuid(),
        baseType: BaseType.identifier,
        cardinality: Cardinality.single
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  },
  [InteractionTypes.textEntry](responseId?: string) {
    return {
      attributes: {
        identifier: responseId ?? uuid(),
        baseType: BaseType.identifier,
        cardinality: Cardinality.single
      },
      correctResponse: {
        value: []
      },
      mapping: {},
      areaMapping: {}
    }
  }
}

export const ERROR_MAP = {
  FETCH_ASSESSMENT_ERROR: "Error fetching assessment."
}

export const INTERACTIONS_TYPE_NAMES: { [key: string]: string } = {
  div: "Text Block",
  choiceInteraction: "Multiple Choice",
  matchInteraction: "Match Table"
}

export const CUSTOM_INTERACTIONS: string[] = [InteractionTypes.equationEditor]

export const DEFAUL_IMAGE_SIZE = 110
export const DEFAULT_CHOICE_IMAGE_SIZE = 200
